/**
 * Set the `x-requested-with` header to help Django's with `request.is_ajax()`.
 */
const ajax = init => {
  init.headers.set('x-requested-with', 'XMLHttpRequest');
  return init;
};

class AuthenticationError extends Error {
  constructor(response) {
    super();
    this.response = response;
  }

}
const authenticationError = res => {
  if (res.status === 401) {
    throw new AuthenticationError(res);
  }

  return res;
};

/**
 * Configure the request to allow passing credentials in cross-origin requests.
 *
 * This is necessary to allow services hosted on subdomains to pass the
 * appropriate cookies to the monolith in order to make authenticated requests.
 */
const cors = init => {
  // Note that we set credentials to `include` for every request as this
  // attribute has no incidence on same-origin requests.
  init.credentials = 'include';
  return init;
};

/** HTTP methods considered safe, not needing CSRF protection. */
const CSRF_SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];
/** Cached token as a promise to avoid cache stampede */

let csrfTokenPromise = null;
/**
 * Return `true` if `method` is considered as a safe CSRF HTTP method.
 */

function isSafeMethod(method) {
  return CSRF_SAFE_METHODS.includes(method || '');
}
/**
 * Return `true` if both `url1` and `url2` have the same origin.
 */


function isSameOrigin(url1, url2) {
  try {
    return new URL(url1).origin === new URL(url2).origin;
  } catch (err) {
    return false;
  }
}
/**
 * Return `true` if the current request needs to pass a CSRF token.
 */


function needsCsrfToken(method, context) {
  // We currently don't support CSRF for non-browser environments.
  if (!context.isBrowser) return false; // Safe methods don't need the CSRF header.

  if (isSafeMethod(method)) return false; // Cross-origin requests external to the `baseURL` don't need a CSRF token.

  if (!isSameOrigin(context.url, context.origin) && !isSameOrigin(context.url, context.baseUrl)) {
    return false;
  }

  return true;
}
/**
 * Extract the CSRF token from the `csrftoken` cookie.
 */


function getCsrfTokenFromCookie() {
  const match = document.cookie.match(/csrftoken=(\w+)/);
  return match ? match[1] : '';
}
/**
 * Fetch the CSRF token from the monolith's CRSF endpoint.
 */


async function fetchCsrfToken(context) {
  const url = "".concat(context.baseUrl || context.origin, "/api/v3/csrf");
  const res = await context.fetcher.call(null, url, {
    credentials: 'include'
  });
  return res.headers.get('x-upstreamcsrftoken');
}
/**
 * Return the CSRF token for the current request.
 *
 * It first tries to read it from the cached resolved token promise. We use a
 * promise here to avoid a cache stampede if multiple requests are made in
 * parallel while fetching the token.
 *
 * If the cache fails, it tries to read the token from the cookie, which should
 * be available for same-origin requests. If the cookie is not present or has
 * expired, it fetches a new CSRF token.
 */


async function negociateCsrfToken(context) {
  if (!csrfTokenPromise) {
    csrfTokenPromise = Promise.resolve(getCsrfTokenFromCookie() || fetchCsrfToken(context));
  }

  return await csrfTokenPromise;
}

function clearCsrfCache() {
  csrfTokenPromise = null;
}
/**
 * Set the `x-csrftoken` header for requests that need a CSRF token.
 */

const csrf = async (init, context) => {
  if (needsCsrfToken(init.method, context)) {
    const csrfToken = await negociateCsrfToken(context);
    clearCsrfCache();
    init.headers.set('x-csrftoken', csrfToken);
  }

  return init;
};

/**
 * Return `true` if `obj` is a plain object.
 *
 * A plain object is an object literal which prototype is the same as `{}`.
 */
function isPlainObject(obj) {
  return obj !== null && typeof obj === 'object' && Object.getPrototypeOf(obj) === Object.getPrototypeOf({});
}
/**
 * Serialize a JSON request `body` and set the appropriate `content-type`
 * header.
 */


const requestBody = async init => {
  if (isPlainObject(init.body) || Array.isArray(init.body)) {
    try {
      init.body = JSON.stringify(init.body);
      init.headers.set('content-type', 'application/json;charset=utf-8');
    } catch (err) {// Silent error
    }
  }

  return init;
};

/**
 * Parse the response's `body` depending on the `content-type` header.
 */
const responseBody = async res => {
  try {
    const mimeType = res.headers.get('content-type') || '';

    if (mimeType.startsWith('application/json')) {
      /* eslint-disable-next-line require-atomic-updates */
      res.body = await res.response.json();
    } else if (mimeType.startsWith('text/')) {
      /* eslint-disable-next-line require-atomic-updates */
      res.body = await res.response.text();
    }
  } catch (err) {// Silent error
  }

  return res;
};

const defaultBeforeRequest = [ajax, cors, csrf, requestBody];
const defaultAfterResponse = [responseBody, authenticationError];

function isBrowser() {
  return typeof window !== 'undefined';
}

const RE_ABSOLUTE_URL = /^(https?:)?\/\//;
/**
 * Resolve `url` to an absolute URL.
 *
 * It prepends `baseUrl` or falls back to `location.origin` otherwise.
 */

function resolveUrl(url, baseUrl) {
  if (RE_ABSOLUTE_URL.exec(url)) return url;
  const prefix = baseUrl || location.origin;
  const sep = url[0] === '/' ? '' : '/';
  return "".concat(prefix).concat(sep).concat(url);
}

async function invokeMiddlewares(middlewares, input, context) {
  return await middlewares.reduce(async (prevPromise, middleware) => await middleware(await prevPromise, context), Promise.resolve(input));
}

/**
 * Normalize the `FetchInit` object.
 *
 * It makes sure that middlewares can expect an object that always has the
 * same shape, and as a consequence remove the need to check for the presence
 * of some attributes (eg. `FetchInit#headers`).
 */
function normalizeInit(init) {
  return {
    method: init.body ? 'POST' : 'GET',
    ...init,
    headers: new Headers(init.headers)
  };
}

function normalizeOptions() {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const fetcher = options.fetcher || (isBrowser() ? window.fetch : undefined);

  if (!fetcher) {
    throw new Error('You need to provide a "fetcher" for server environments');
  }

  const baseUrl = process && process.env.ZAPIER_FETCH_BASE_URL || process && process.env.NEXT_PUBLIC_ZAPIER_FETCH_BASE_URL || options.baseUrl || '';
  return {
    baseUrl,
    fetcher,
    beforeRequest: options.beforeRequest || [...defaultBeforeRequest],
    afterResponse: options.afterResponse || [...defaultAfterResponse]
  };
}

/**
 * Create the `Context` object for the current request.
 *
 * The context holds auxiliary information about the request that is to be
 * used by middlewares.
 */

function createContext(url, baseUrl, fetcher) {
  return {
    url: resolveUrl(url, baseUrl),
    baseUrl,
    origin: location.origin,
    isBrowser: isBrowser(),
    fetcher
  };
}
/**
 * Create the `FetchResponse` object.
 *
 * `FetchResponse` exposes the most used attributes of `Response`. The original
 * response object is exposed by `FetchResponse#response` for advanced usage.
 */


function createResponse(response) {
  // Some `Response` properties are not enumerable so we explicitely copy them
  return {
    ok: response.ok,
    status: response.status,
    headers: response.headers,
    body: response.body,
    response
  };
}
/**
 * Create a custom `fetch` function configured with `options`.
 *
 * The configured `fetch` function does the following internally:
 *
 *   1. Create the request's context for middlewares.
 *   2. Invoke request middlewares.
 *   3. Fetch the response using the provided `fetcher`.
 *   4. Invoke response middlewares.
 */
// @ts-ignore


const createFetch = options => {
  const optionsFactory = (url, init) => normalizeOptions(typeof options === 'function' ? options(url, init) : options);

  return async function fetch(url) {
    let init = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const {
      baseUrl,
      fetcher,
      beforeRequest,
      afterResponse
    } = optionsFactory(url, init);
    const context = createContext(url, baseUrl, fetcher);
    const finalInit = await invokeMiddlewares(beforeRequest, normalizeInit(init), context);
    const res = await context.fetcher.call(null, context.url, finalInit);
    const finalRes = await invokeMiddlewares(afterResponse, createResponse(res), context);
    return finalRes;
  };
};

/**
 * Default `fetch` function configured for a typical usage.
 */

const fetch = createFetch();

export default fetch;
export { AuthenticationError, ajax, authenticationError, clearCsrfCache, cors, createFetch, csrf, defaultAfterResponse, defaultBeforeRequest, requestBody, responseBody };
